// SPDX-License-Identifier: AGPL-3.0-or-later
// Based on au-births by Ben Richardson — https://benrichardson.dev
// Data types, loading and small formatting/lookup helpers.

export interface AgeGroup { id: string; label: string }

export interface RegionSummary {
  births: (number | null)[];
  cbr: (number | null)[];
  nuptial: (number | null)[];
  exNuptial: (number | null)[];
  population: (number | null)[];
  tfr: (number | null)[];
  medMother: (number | null)[];
  medFather: (number | null)[];
  medFirstMother: (number | null)[];
  medDuration: (number | null)[];
}

export interface BirthsData {
  years: number[];
  states: string[];
  regionNames: Record<string, string>;
  ageGroups: AgeGroup[];
  months: string[];
  replacementRate: number;
  summary: Record<string, RegionSummary>;
  asfr: Record<string, Record<string, (number | null)[]>>;
  season: Record<string, (number | null)[][]>;
  seasonComplete: boolean[];
  lastCompleteSeason: number | null;
}

export interface Meta {
  dataFrom: number;
  dataThrough: number;
  replacementRate: number;
  tfr: { first: number; latest: number; min: number; minYear: number; belowSince: number | null };
  births: { first: number; latest: number; peak: number; peakYear: number };
  medianAgeMother: { first: number; latest: number };
  generated: string;
}

export interface Loaded { data: BirthsData; meta: Meta }

export async function loadData(): Promise<Loaded> {
  const [data, meta] = await Promise.all([
    fetch('data/births.json').then((r) => { if (!r.ok) throw new Error('births.json'); return r.json() as Promise<BirthsData>; }),
    fetch('data/meta.json').then((r) => { if (!r.ok) throw new Error('meta.json'); return r.json() as Promise<Meta>; }),
  ]);
  return { data, meta };
}

// ── Region ordering & colours ──────────────────────────────────────────────
export const REGION_ORDER = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

/** One stable colour per state, used identically across every view. */
export const REGION_COLOUR: Record<string, string> = {
  AUS: '#0f766e',
  NSW: '#2563eb',
  VIC: '#7c3aed',
  QLD: '#db2777',
  SA: '#d97706',
  WA: '#0891b2',
  TAS: '#16a34a',
  NT: '#dc2626',
  ACT: '#4f46e5',
};

export const AGE_COLOUR: Record<string, string> = {
  A15: '#f59e0b',
  A20: '#f97316',
  A25: '#ef4444',
  A30: '#0d9488',
  A35: '#2563eb',
  A40: '#7c3aed',
  A45: '#64748b',
};

// ── Formatting ─────────────────────────────────────────────────────────────
export const fmtInt = (n: number | null | undefined): string =>
  n == null || !Number.isFinite(n) ? '—' : Math.round(n).toLocaleString('en-AU');

export function fmt(n: number | null | undefined, dp = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export const fmtPct = (n: number | null | undefined, dp = 1): string =>
  n == null || !Number.isFinite(n) ? '—' : `${fmt(n, dp)}%`;

export function fmtSigned(n: number | null | undefined, dp = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const s = fmt(Math.abs(n), dp);
  return n > 0 ? `+${s}` : n < 0 ? `−${s}` : s;
}

/** Escape for use inside a data-tip attribute (newlines become literal &#10;). */
export function tipAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '&#10;');
}

/** Escape for HTML text content. */
export function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ── Small series helpers ───────────────────────────────────────────────────
export const lastVal = (a: (number | null)[]): number | null => {
  for (let i = a.length - 1; i >= 0; i--) if (a[i] != null) return a[i];
  return null;
};

export const firstVal = (a: (number | null)[]): number | null => {
  for (let i = 0; i < a.length; i++) if (a[i] != null) return a[i];
  return null;
};

export function exNuptialShare(s: RegionSummary): (number | null)[] {
  return s.births.map((b, i) => {
    const e = s.exNuptial[i];
    return b != null && b > 0 && e != null ? (e / b) * 100 : null;
  });
}

export function regionName(data: BirthsData, code: string): string {
  return data.regionNames[code] ?? code;
}
